import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const APITest = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    testPublicEndpoints();
  }, []);

  const testPublicEndpoints = async () => {
    const results = {};

    // Test 1: Get courses (public endpoint)
    try {
      console.log('Testing public courses endpoint...');
      const coursesData = await apiService.getCourses();
      results.courses = {
        success: true,
        count: coursesData.length,
        data: coursesData.slice(0, 2) // Show first 2 courses
      };
      setCourses(coursesData);
    } catch (error) {
      console.error('Courses test failed:', error);
      results.courses = {
        success: false,
        error: error.message
      };
    }

    // Test 2: Get course details (public endpoint)
    try {
      console.log('Testing public course details endpoint...');
      if (courses.length > 0) {
        const courseDetails = await apiService.getCourseDetails(courses[0].id);
        results.courseDetails = {
          success: true,
          data: courseDetails
        };
      } else {
        results.courseDetails = {
          success: false,
          error: 'No courses available to test'
        };
      }
    } catch (error) {
      console.error('Course details test failed:', error);
      results.courseDetails = {
        success: false,
        error: error.message
      };
    }

    // Test 3: Test with invalid token (should still work for public endpoints)
    try {
      console.log('Testing public endpoints with invalid token...');
      // Temporarily set an invalid token
      const originalToken = localStorage.getItem('authToken');
      localStorage.setItem('authToken', 'invalid-token');
      
      const coursesWithInvalidToken = await apiService.getCourses();
      results.invalidTokenTest = {
        success: true,
        count: coursesWithInvalidToken.length
      };
      
      // Restore original token
      if (originalToken) {
        localStorage.setItem('authToken', originalToken);
      } else {
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Invalid token test failed:', error);
      results.invalidTokenTest = {
        success: false,
        error: error.message
      };
    }

    setTestResults(results);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-4">API Test Results</h2>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p>Testing public endpoints...</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">API Test Results</h2>
      
      <div className="space-y-6">
        {/* Test 1: Courses */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">Test 1: Public Courses Endpoint</h3>
          <div className={`p-3 rounded ${testResults.courses?.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {testResults.courses?.success ? (
              <div>
                <p className="font-medium">✅ Success!</p>
                <p>Found {testResults.courses.count} courses</p>
                {testResults.courses.data && (
                  <div className="mt-2">
                    <p className="text-sm">Sample courses:</p>
                    <ul className="text-sm list-disc list-inside ml-4">
                      {testResults.courses.data.map(course => (
                        <li key={course.id}>{course.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="font-medium">❌ Failed</p>
                <p>Error: {testResults.courses?.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Test 2: Course Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">Test 2: Public Course Details Endpoint</h3>
          <div className={`p-3 rounded ${testResults.courseDetails?.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {testResults.courseDetails?.success ? (
              <div>
                <p className="font-medium">✅ Success!</p>
                <p>Course: {testResults.courseDetails.data?.name}</p>
                <p className="text-sm">Description: {testResults.courseDetails.data?.description}</p>
              </div>
            ) : (
              <div>
                <p className="font-medium">❌ Failed</p>
                <p>Error: {testResults.courseDetails?.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Test 3: Invalid Token */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">Test 3: Public Endpoints with Invalid Token</h3>
          <div className={`p-3 rounded ${testResults.invalidTokenTest?.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {testResults.invalidTokenTest?.success ? (
              <div>
                <p className="font-medium">✅ Success!</p>
                <p>Public endpoints work even with invalid token</p>
                <p>Found {testResults.invalidTokenTest.count} courses</p>
              </div>
            ) : (
              <div>
                <p className="font-medium">❌ Failed</p>
                <p>Error: {testResults.invalidTokenTest?.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-3">Summary</h3>
          <div className="space-y-2">
            <p><strong>Courses Endpoint:</strong> {testResults.courses?.success ? '✅ Working' : '❌ Failed'}</p>
            <p><strong>Course Details Endpoint:</strong> {testResults.courseDetails?.success ? '✅ Working' : '❌ Failed'}</p>
            <p><strong>Public Access:</strong> {testResults.invalidTokenTest?.success ? '✅ Working' : '❌ Failed'}</p>
          </div>
          
          {testResults.courses?.success && testResults.courseDetails?.success && testResults.invalidTokenTest?.success ? (
            <div className="mt-4 p-3 bg-green-100 text-green-800 rounded">
              <p className="font-medium">🎉 All tests passed! Public endpoints are working correctly.</p>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-yellow-100 text-yellow-800 rounded">
              <p className="font-medium">⚠️ Some tests failed. Check the backend connection and API endpoints.</p>
            </div>
          )}
        </div>

        {/* Backend Status */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-3">Backend Status</h3>
          <p className="text-sm text-gray-600">
            Make sure your Django backend is running on <code className="bg-gray-200 px-2 py-1 rounded">http://localhost:8000</code>
          </p>
          <p className="text-sm text-gray-600 mt-2">
            API Base URL: <code className="bg-gray-200 px-2 py-1 rounded">{process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default APITest;
